import { useState, useEffect, useRef } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Image, ChevronLeft, ChevronRight, Loader2, X, Upload, Clock, Link2, FileVideo, FileImage } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";

const PENDING_PLACEHOLDER = "/api/files/placeholder";

function MemeCard({ meme, onClick }) {
  const isPending = meme.status === "pending";
  const isVideo = meme.media_type === "video";

  return (
    <div
      className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer relative"
      onClick={() => !isPending && onClick(meme)}
      data-testid={`meme-card-${meme.id}`}
    >
      {isPending && (
        <div className="absolute inset-0 z-10 bg-slate-900/60 flex flex-col items-center justify-center backdrop-blur-[2px]">
          <Clock className="w-8 h-8 text-amber-400 mb-2" />
          <span className="text-amber-400 font-bold text-sm" style={{ fontFamily: "Barlow Condensed" }} data-testid="pending-badge">
            TO BE APPROVED
          </span>
          <span className="text-white/60 text-xs mt-1">Submitted by {meme.submitted_by || "User"}</span>
        </div>
      )}

      <div className="aspect-square relative overflow-hidden bg-slate-100">
        {isVideo && !isPending ? (
          <video
            src={meme.image_url}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            onMouseOver={e => e.target.play()}
            onMouseOut={e => { e.target.pause(); e.target.currentTime = 0; }}
            data-testid={`meme-video-${meme.id}`}
          />
        ) : (
          <img
            src={isPending ? undefined : meme.image_url}
            alt={meme.title || "West Ham meme"}
            className={`w-full h-full object-cover ${isPending ? "blur-sm opacity-30" : "group-hover:scale-105"} transition-transform duration-300`}
            loading="lazy"
            onError={e => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23f1f5f9' width='400' height='400'/%3E%3Ctext x='200' y='200' text-anchor='middle' fill='%2394a3b8' font-size='16'%3EImage%3C/text%3E%3C/svg%3E"; }}
          />
        )}
        {isVideo && !isPending && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <FileVideo className="w-3 h-3" /> Video
          </div>
        )}
      </div>

      <div className="p-4">
        {meme.title && (
          <h3 className="text-sm font-bold text-[#0F172A] mb-1 line-clamp-2" style={{ fontFamily: "Barlow Condensed" }}>
            {meme.title}
          </h3>
        )}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="capitalize">{meme.source || "Community"}</span>
          <span>{new Date(meme.date_added).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
        </div>
      </div>
    </div>
  );
}

function SubmitModal({ onClose, onSubmit }) {
  const [mode, setMode] = useState("file"); // "file" or "url"
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [mediaType, setMediaType] = useState("image");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith("video/")) setMediaType("video");
    else setMediaType("image");
  };

  const handleSubmit = async () => {
    setError("");
    setUploading(true);
    try {
      let imageUrl = "";
      let mType = mediaType;

      if (mode === "file" && file) {
        // Upload file first
        const formData = new FormData();
        formData.append("file", file);
        const uploadRes = await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        imageUrl = `/api/files/${uploadRes.data.storage_path}`;
        if (file.type.startsWith("video/")) mType = "video";
      } else if (mode === "url" && url.trim()) {
        imageUrl = url.trim();
        // Auto-detect media type from URL
        if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) mType = "video";
      } else {
        setError("Please select a file or enter a URL");
        setUploading(false);
        return;
      }

      await onSubmit({ title, image_url: imageUrl, media_type: mType });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="submit-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Barlow Condensed" }}>SUBMIT A MEME</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200" data-testid="submit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode("file")}
            className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${mode === "file" ? "bg-[#7C2C3B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="mode-file"
          >
            <Upload className="w-4 h-4" /> Upload File
          </button>
          <button
            onClick={() => setMode("url")}
            className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${mode === "url" ? "bg-[#7C2C3B] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="mode-url"
          >
            <Link2 className="w-4 h-4" /> Paste URL
          </button>
        </div>

        {/* Title */}
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Give your meme a title (optional)"
          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#7C2C3B]/30"
          data-testid="submit-title"
        />

        {/* File Upload */}
        {mode === "file" && (
          <div className="mb-4">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-[#7C2C3B]/50 transition-colors"
              data-testid="file-picker"
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  {file.type.startsWith("video/") ? <FileVideo className="w-5 h-5 text-[#7C2C3B]" /> : <FileImage className="w-5 h-5 text-[#7C2C3B]" />}
                  <span className="text-sm text-[#0F172A] font-medium">{file.name}</span>
                  <span className="text-xs text-slate-400">({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm text-slate-500">Click to select an image or video</p>
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF, WebP, MP4, WebM (max 50MB)</p>
                </div>
              )}
            </button>
          </div>
        )}

        {/* URL Input */}
        {mode === "url" && (
          <div className="mb-4">
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="Paste image or video URL..."
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#7C2C3B]/30"
              data-testid="submit-url"
            />
            <p className="text-xs text-slate-400 mt-1">Supports image and video URLs (.jpg, .png, .mp4, etc.)</p>
          </div>
        )}

        {error && <p className="text-red-500 text-sm mb-3" data-testid="submit-error">{error}</p>}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-[#7C2C3B] text-white hover:bg-[#6B2533] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading..." : "Submit for Approval"}
          </button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-3">
          Your submission will appear with a "To Be Approved" badge until reviewed by an admin.
        </p>
      </div>
    </div>
  );
}

export default function MemesPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ memes: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);

  const fetchMemes = () => {
    setLoading(true);
    axios.get(`${API}/memes?page=${page}&per_page=10`)
      .then(res => setData(res.data))
      .catch(() => setData({ memes: [], total: 0, page: 1, pages: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMemes(); }, [page]);

  const handleSubmit = async (memeData) => {
    await axios.post(`${API}/memes/submit`, memeData);
    fetchMemes();
  };

  const jsonLd = data.memes.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "West Ham Memes",
    "description": "The best West Ham United memes from across the internet. Updated daily with fresh content from the Hammers fan community.",
    "url": "https://allwhufc.com/west-ham-memes",
    "numberOfItems": data.total,
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="memes-page">
      <SEO
        title="West Ham Memes"
        path="/west-ham-memes"
        description="The best West Ham United memes from across the internet. Fresh daily content from the Hammers fan community on TikTok, Instagram, X and Facebook."
        jsonLd={jsonLd}
      />
      <Navbar />

      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: "Barlow Condensed" }} data-testid="memes-title">
              WEST HAM MEMES
            </h1>
            <p className="text-white/80">The best Hammers memes from around the internet</p>
          </div>
          {user && (
            <button
              onClick={() => setShowSubmit(true)}
              className="bg-white text-[#7C2C3B] px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-slate-100 transition-colors flex items-center gap-2"
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid="submit-meme-btn"
            >
              <Upload className="w-4 h-4" /> SUBMIT A MEME
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!user && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-center">
            <p className="text-amber-800 text-sm">
              <a href="/login" className="font-bold underline hover:no-underline">Sign in</a> to submit your own memes to the collection!
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="memes-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" />
          </div>
        ) : data.memes.length === 0 ? (
          <div className="text-center py-16" data-testid="memes-empty">
            <Image className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-bold text-slate-400 mb-2" style={{ fontFamily: "Barlow Condensed" }}>
              NO MEMES YET
            </h3>
            <p className="text-slate-400 text-sm">
              Fresh memes are on their way. Check back soon!
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="memes-grid">
              {data.memes.map((meme) => (
                <MemeCard key={meme.id} meme={meme} onClick={setLightbox} />
              ))}
            </div>

            {/* Pagination */}
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8" data-testid="memes-pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors disabled:opacity-40"
                  data-testid="memes-prev"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600" />
                </button>
                <span className="text-sm text-slate-600 font-medium">
                  Page {data.page} of {data.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                  disabled={page >= data.pages}
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors disabled:opacity-40"
                  data-testid="memes-next"
                >
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>
              </div>
            )}

            <div className="mt-6 text-center text-sm text-slate-400">
              {data.total} meme{data.total !== 1 ? "s" : ""} in the collection
            </div>

            <SocialShareButtons shareText="Check out the best West Ham memes on All WHUFC!" />
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="meme-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors z-10"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-3xl max-h-[85vh]" onClick={e => e.stopPropagation()}>
            {lightbox.media_type === "video" ? (
              <video
                src={lightbox.image_url}
                className="max-w-full max-h-[80vh] rounded-lg"
                controls
                autoPlay
                data-testid="lightbox-video"
              />
            ) : (
              <img
                src={lightbox.image_url}
                alt={lightbox.title || "West Ham meme"}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            )}
            {lightbox.title && (
              <p className="text-white text-center mt-3 text-sm font-medium">{lightbox.title}</p>
            )}
          </div>
        </div>
      )}

      {/* Submit Modal */}
      {showSubmit && <SubmitModal onClose={() => setShowSubmit(false)} onSubmit={handleSubmit} />}

      <Footer />
    </div>
  );
}
